// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Column,
  Button,
  InlineNotification,
  Tag,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Breadcrumb,
  BreadcrumbItem,
  ProgressBar,
  Tile,
  Modal,
  TextArea,
} from '@carbon/react';
import { Group, DocumentEdit, CheckmarkFilled, InProgress, Warning, Time, User } from '@carbon/icons-react';
import { useAuth } from '../../hooks/useAuth';
import { useCollaboration } from '../../hooks/useCollaboration';
import CollaborationSession from '../../components/collaboration/CollaborationSession';
import CollaborativeEditor from '../../components/collaboration/CollaborativeEditor';
import ParticipantsList from '../../components/collaboration/ParticipantsList';
import AnnotationLayer from '../../components/collaboration/AnnotationLayer';

interface Assignment {
  id: string;
  title: string;
  description: string;
  dueDate: string;
  groupSize: number;
  status: 'not_started' | 'in_progress' | 'submitted' | 'graded';
  grade?: number;
  feedback?: string;
}

const GroupAssignment: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [submissionNotes, setSubmissionNotes] = useState('');

  const {
    participants,
    isConnected,
    isReconnecting,
    connectionError,
    edits,
    conflicts,
    annotations,
    capabilities,
    sendEdit,
    createAnnotation,
    resolveConflict,
  } = useCollaboration({
    sessionId: `assignment-${id}`,
    autoConnect: true,
    onConnect: () => console.log('Connected to group assignment'),
    onDisconnect: () => console.log('Disconnected from group assignment'),
    onError: error => console.error('Assignment collaboration error:', error),
  });

  useEffect(() => {
    const fetchAssignment = async () => {
      try {
        const response = await fetch(`/api/v1/assignments/${id}`, {
          headers: {
            Authorization: `Bearer ${user?.token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setAssignment(data);
        } else {
          console.error('Failed to fetch assignment');
        }
      } catch (error) {
        console.error('Error fetching assignment:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchAssignment();
    }
  }, [id, user]);

  const submitAssignment = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(`/api/v1/assignments/${id}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user?.token}`,
        },
        body: JSON.stringify({
          content: edits.map(e => e.content).join('\n'),
          notes: submissionNotes,
          groupMembers: participants.map(p => p.id),
        }),
      });

      if (response.ok) {
        setAssignment(prev => (prev ? { ...prev, status: 'submitted' } : null));
        setSubmitModalOpen(false);
      } else {
        console.error('Failed to submit assignment');
      }
    } catch (error) {
      console.error('Error submitting assignment:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusColor = (status: Assignment['status']) => {
    switch (status) {
      case 'not_started':
        return 'cool-gray';
      case 'in_progress':
        return 'blue';
      case 'submitted':
        return 'green';
      case 'graded':
        return 'purple';
      default:
        return 'cool-gray';
    }
  };

  const getTimeRemaining = () => {
    if (!assignment) return '';
    const due = new Date(assignment.dueDate);
    const now = new Date();
    const diff = due.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (diff < 0) return 'Overdue';
    if (days > 0) return `${days} days, ${hours} hours`;
    return `${hours} hours`;
  };

  if (loading) {
    return <div>Loading assignment...</div>;
  }

  if (!assignment) {
    return <div>Assignment not found</div>;
  }

  return (
    <div className="group-assignment">
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <Breadcrumb>
            <BreadcrumbItem href="/">Home</BreadcrumbItem>
            <BreadcrumbItem href="/assignments">Assignments</BreadcrumbItem>
            <BreadcrumbItem isCurrentPage>{assignment.title}</BreadcrumbItem>
          </Breadcrumb>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <div className="assignment-header">
            <h1>{assignment.title}</h1>
            <div className="assignment-meta">
              <Tag
                type={getStatusColor(assignment.status)}
                aria-label={`Assignment status: ${assignment.status.replace('_', ' ')}`}
              >
                {assignment.status.replace('_', ' ')}
              </Tag>
              <Tag type="outline">
                <Group size={16} />
                Group of {assignment.groupSize}
              </Tag>
              <Tag type="outline">
                <Time size={16} />
                {getTimeRemaining()}
              </Tag>
            </div>
          </div>
        </Column>

        <Column lg={16} md={8} sm={4}>
          <Tile>
            <h4>Assignment Description</h4>
            <p>{assignment.description}</p>
            {assignment.grade && (
              <div className="grade-feedback">
                <h5>Grade: {assignment.grade}%</h5>
                {assignment.feedback && <p>{assignment.feedback}</p>}
              </div>
            )}
          </Tile>
        </Column>

        {connectionError && (
          <Column lg={16} md={8} sm={4}>
            <div role="alert" aria-live="assertive">
              <InlineNotification
                kind="error"
                title="Connection Error"
                subtitle={connectionError.message}
                lowContrast
                aria-describedby="group-connection-error"
              />
              <span id="group-connection-error" className="sr-only">
                Connection error: {connectionError.message}
              </span>
            </div>
          </Column>
        )}

        <Column lg={12} md={6} sm={4}>
          <div className="assignment-workspace">
            <div className="workspace-header">
              <h3>Group Workspace</h3>
              <div className="connection-status" role="status" aria-live="polite" aria-atomic="true">
                {isConnected ? (
                  <Tag type="green" aria-label="Collaboration connected">
                    <CheckmarkFilled size={16} />
                    Connected
                  </Tag>
                ) : isReconnecting ? (
                  <Tag type="cool-gray">
                    <InProgress size={16} />
                    Reconnecting...
                  </Tag>
                ) : (
                  <Tag type="red">
                    <Warning size={16} />
                    Disconnected
                  </Tag>
                )}
              </div>
            </div>

            <CollaborationSession
              sessionId={`assignment-${id}`}
              userId={user?.id || ''}
              token={user?.token || ''}
              mode="participant"
              sessionTitle={assignment.title}
            >
              <CollaborativeEditor
                onEdit={(content, position) => sendEdit(content, position)}
                edits={edits}
                conflicts={conflicts}
                onResolveConflict={resolveConflict}
                readOnly={assignment.status === 'submitted' || assignment.status === 'graded'}
              />
              <AnnotationLayer
                annotations={annotations}
                onCreateAnnotation={createAnnotation}
                selections={[]}
                onShareSelection={() => {}}
              />
            </CollaborationSession>

            {assignment.status === 'in_progress' && capabilities.canEdit && (
              <div className="submission-controls">
                <Button kind="primary" onClick={() => setSubmitModalOpen(true)} disabled={!isConnected}>
                  Submit Assignment
                </Button>
                <Button kind="secondary">Save Draft</Button>
              </div>
            )}
          </div>
        </Column>

        <Column lg={4} md={2} sm={4}>
          <div className="assignment-sidebar">
            <div className="group-members">
              <h4>Group Members</h4>
              <ParticipantsList participants={participants} currentUserId={user?.id} />
              <div className="member-activity">
                <h5>Activity</h5>
                <div className="activity-list">
                  {participants.map(p => (
                    <div key={p.id} className="activity-item">
                      <User size={16} />
                      <span>{p.name}</span>
                      <Tag type={p.isActive ? 'green' : 'cool-gray'} size="sm">
                        {p.isActive ? 'Active' : 'Away'}
                      </Tag>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="assignment-progress">
              <h4>Progress</h4>
              <ProgressBar label="Completion" value={edits.length > 0 ? 65 : 0} max={100} />
              <div className="progress-stats">
                <p>Words written: {edits.reduce((acc, e) => acc + e.content.split(' ').length, 0)}</p>
                <p>Edits made: {edits.length}</p>
                <p>Comments: {annotations.filter(a => a.type === 'note').length}</p>
              </div>
            </div>

            {user?.role === 'teacher' && (
              <div className="teacher-tools">
                <h4>Teacher Tools</h4>
                <Button kind="ghost" size="sm">
                  View All Groups
                </Button>
                <Button kind="ghost" size="sm">
                  Send Feedback
                </Button>
                <Button kind="ghost" size="sm">
                  Export Submissions
                </Button>
              </div>
            )}
          </div>
        </Column>
      </Grid>

      <Modal
        open={submitModalOpen}
        onRequestClose={() => setSubmitModalOpen(false)}
        modalHeading="Submit Group Assignment"
        primaryButtonText="Submit"
        secondaryButtonText="Cancel"
        primaryButtonDisabled={submitting}
        onRequestSubmit={submitAssignment}
      >
        <p>Are you sure you want to submit this assignment? Once submitted, you cannot make further changes.</p>
        <TextArea
          id="submission-notes"
          labelText="Submission Notes (optional)"
          placeholder="Any additional notes or comments about your submission..."
          value={submissionNotes}
          onChange={e => setSubmissionNotes(e.target.value)}
          rows={4}
        />
        <div className="submission-checklist">
          <h5>Submission Checklist:</h5>
          <ul>
            <li>All group members have reviewed the work</li>
            <li>All requirements have been addressed</li>
            <li>Citations and references are included</li>
            <li>Work has been proofread</li>
          </ul>
        </div>
      </Modal>
    </div>
  );
};

export default GroupAssignment;
